import { AppGrid } from './grid.component';
import { SubSink } from 'subsink';
import { Directive, inject } from '@angular/core';
import { GridHeader, GridPreferences } from './grid.dto';
import { UntypedFormBuilder } from '@angular/forms';
import { BreakpointObserver } from '@angular/cdk/layout';
import { merge } from 'lodash-es';
import { Dto, Query } from '@damofire/data-commons';
import { EmitterService } from '../emitter.service';
import { ObservableQueryService, StorageService } from '@damofire/data-angular-commons';
import { Firestore } from '@angular/fire/firestore';
import { doc, DocumentReference, DocumentSnapshot, onSnapshot } from 'firebase/firestore';
import { HIDE_PREVIEW_MENU, MainLayoutComponent } from '../main-layout/main-layout.component';
import { findDtoIndex } from '../forms/form-utilities';
import { SortOption } from './table-sort-options/table-sort-options.component';
import { TranslateService } from '@ngx-translate/core';

@Directive()
export abstract class AngularFireGrid<T extends Dto<any>> extends AppGrid<T> {
  protected readonly docsSubs = new SubSink();
  protected searchDocs: { [id: string]: DocumentReference };
  selectedDocs: DocumentReference<any>[];
  private readonly firestore = inject(Firestore);

  protected constructor(
    protected readonly emitterService: EmitterService,
    protected readonly collection: string,
    protected readonly prefStorageKey: string,
    protected readonly headerColumns: GridHeader[],
    public readonly itemsSortsChoices: SortOption[],
    protected readonly defaultPreferences: GridPreferences,
    protected readonly storage: StorageService,
    protected readonly formBuilder: UntypedFormBuilder,
    protected readonly breakpointObserver: BreakpointObserver,
    protected readonly queryService: ObservableQueryService,
    protected readonly translate: TranslateService,
    protected readonly stateChangeMonitorQueryField = 'audit.createdDate',
  ) {
    super(emitterService, collection, prefStorageKey, headerColumns, itemsSortsChoices, defaultPreferences, storage, formBuilder, breakpointObserver, translate);
  }

  clearSelection(): void {
    super.clearSelection();

    this.selectedDocs = [];
    this.gridPreferences.displayedDocs = [];
    this.storage.setObject(this.prefStorageKey, this.gridPreferences);
  }

  protected selectionChanged(): void {
    this.syncSelectedDocs();
  }

  private syncSelectedDocs(): void {
    this.selectedDocs = [];
    const selectedDocsPath = [] as string[];

    const currentSelection = this.selection();
    currentSelection?.forEach((s) => {
      const matchingDoc = this.searchDocs[s._id];
      this.selectedDocs.push(matchingDoc);
      if (matchingDoc) {
        selectedDocsPath.push(matchingDoc.path);
      }
    });
    this.gridPreferences.displayedDocs = selectedDocsPath;
    this.storage.setObject(this.prefStorageKey, this.gridPreferences);
  }

  updateSelectedDoc(docRef: DocumentReference): void {
    const matchingInx = this.selectedDocs?.findIndex((d) => d.id === docRef.id) || -1;

    if (matchingInx === -1) {
      const matchingDataInx = super.getSearchResult().data.findIndex((r) => r._id === docRef.id);
      this.updateSelection(super.getSearchResult().data[matchingDataInx]);
    }
  }

  protected afterSearch(query?: Query): void {
    super.afterSearch(query);
  }

  protected prepareForSearch(silent = false): void {
    this.searchDocs = {};
    super.prepareForSearch(silent);
  }

  protected docRef(id: string): DocumentReference<T> {
    return doc(this.firestore, `${this.collection}/${id}`) as DocumentReference<T>;
  }

  protected mapSearchResult(result: T[]): T[] {
    this.docsSubs.unsubscribe();
    result.forEach((element: T) => {
      const docRef = this.docRef(element._id);
      this.searchDocs[element._id] = docRef;
      const unsubCb = onSnapshot(docRef, (snapshot: DocumentSnapshot) => {
        if (snapshot.exists()) {
          merge(element, { ...snapshot.data(), _id: snapshot.id });
          const docIsDisplayed = this.gridPreferences.displayedDocs?.findIndex((dpath) => dpath === docRef.path) > -1;
          const emptySelection = this.selectedDocs?.length <= 1 || this.selectedDocs === undefined;

          if (docIsDisplayed && emptySelection) {
            this.updateSelection(element);
          }
        } else {
          const currentSelection = this.selection();
          const inx = findDtoIndex(currentSelection, element);
          if (inx !== -1) {
            const newSelection = currentSelection.filter((_, i) => i !== inx);
            this.selection.set(newSelection);
            if (!newSelection.length) {
              this.emitterService.get(MainLayoutComponent.channelName()).next({ event: HIDE_PREVIEW_MENU });
            }
          }

          console.log(`Doc was deleted, refreshing list. [${element._id}]`);
          this.search(true);
        }
      });

      this.docsSubs.add({ unsubscribe: unsubCb });
    });

    return result;
  }

  override ngOnDestroy(): void {
    this.docsSubs.unsubscribe();
    super.ngOnDestroy();
  }
}
